import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { IntegrityScore } from './IntegrityScore';
import { RiskBadge } from './RiskBadge';
import { IntegrityRadarChart, ScoreBreakdownChart, WeightBreakdownChart } from './Charts';
import TseDataPanel from './TseDataPanel';
import { Scale, Building2, DollarSign, Newspaper, ExternalLink, User2, Download, ArrowLeft, Share2, FileText, Copy, Check, Loader2, Database, BarChart3, MessageCircle, Twitter, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ExplainButton from './ExplainButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Section = ({ title, children }) => (
  <div className="mb-5">
    <h4 className="text-base font-semibold text-[#0F1C2E] mb-3">{title}</h4>
    {children}
  </div>
);

const ItemList = ({ items, borderColor = '#E2E8F0' }) => {
  if (!items || items.length === 0) return <p className="text-sm text-[#94A3B8] italic">Nenhum dado disponivel</p>;
  return (
    <ul className="space-y-2">
      {items.map((item, idx) => (
        <li key={idx} className="text-sm text-[#475569] pl-4 border-l-2" style={{ borderColor }}>
          {item}
        </li>
      ))}
    </ul>
  );
};

const CandidateProfile = ({ candidate, onBack }) => {
  const { token, isPro, user } = useAuth();
  const navigate = useNavigate();
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  if (!candidate) return null;

  const legal = candidate.legal_status || {};
  const political = candidate.political_history || {};
  const assets = candidate.assets || {};
  const reputation = candidate.reputation || {};

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(candidate, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `todeolho-${candidate.name.replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('JSON exportado!');
  };

  const handleExportPDF = async () => {
    if (!candidate.id) { toast.error('Candidato sem ID para exportar'); return; }
    if (!user) {
      toast.info('Faça login para exportar em PDF.');
      navigate('/auth');
      return;
    }
    if (!isPro) {
      toast.info('Exportação em PDF é exclusiva do Plano Anual (R$ 14,90).');
      navigate('/planos');
      return;
    }
    setPdfLoading(true);
    try {
      const resp = await axios.get(`${API}/candidates/${candidate.id}/pdf`, {
        responseType: 'blob',
        timeout: 30000,
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `todeolho-${candidate.name.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF exportado!');
    } catch (err) {
      console.error('PDF export error:', err);
      const status = err?.response?.status;
      if (status === 402 || status === 401) {
        toast.info('Ative o Plano Anual (R$ 14,90) para liberar a exportação em PDF.');
        navigate('/planos');
      } else {
        toast.error('Erro ao exportar PDF');
      }
    } finally {
      setPdfLoading(false);
    }
  };

  const handleShare = async () => {
    if (!candidate.id) { toast.error('Candidato sem ID para compartilhar'); return; }
    setSharing(true);
    try {
      const resp = await axios.post(`${API}/candidates/${candidate.id}/share`, {}, { timeout: 10000 });
      const link = `${window.location.origin}/share/${resp.data.share_id}`;
      setShareUrl(link);
      toast.success('Link de compartilhamento criado!');
    } catch (err) {
      console.error('Share error:', err);
      toast.error('Erro ao criar link');
    } finally {
      setSharing(false);
    }
  };

  const handleCopyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleWhatsApp = () => {
    const score = (candidate.integrity_score || 0).toFixed(1);
    const risk = candidate.risk_classification || '';
    const name = candidate.name || 'Candidato';
    const link = shareUrl || window.location.href;
    const text = `Confira a analise de integridade de *${name}* no Tô de Olho:\n\nScore: ${score}/10 - ${risk}\nPartido: ${candidate.current_party || 'N/A'}\nCargo: ${candidate.position || 'N/A'}\n\n${link}`;
    const encoded = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
  };

  const handleTwitter = () => {
    const score = (candidate.integrity_score || 0).toFixed(1);
    const risk = candidate.risk_classification || '';
    const name = candidate.name || 'Candidato';
    const link = shareUrl || window.location.href;
    const text = `Confira a analise de ${name} no @TodeOlhoApp - Score de integridade: ${score}/10 (${risk}) ${link} #TôDeOlho #FichaLimpa #Eleicoes`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleMonitor = async () => {
    if (!candidate.id) return;
    try {
      await axios.post(`${API}/watchlist`, {
        candidate_id: candidate.id,
        candidate_name: candidate.name,
        candidate_state: candidate.state,
      }, { timeout: 10000 });
      toast.success(`${candidate.name} adicionado ao monitoramento!`);
    } catch {
      toast.error('Erro ao adicionar ao monitoramento');
    }
  };

  return (
    <div className="space-y-5 fade-in-up pb-20" data-testid="candidate-profile">
      {/* Header */}
      <Card className="p-4 sm:p-6 bg-white border border-[#E2E8F0] rounded-md shadow-sm">
        <div className="flex flex-col gap-3 sm:gap-4">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#1E3A5F] w-fit" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
          )}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-[#F4F6F8] flex items-center justify-center shrink-0">
                <User2 className="w-8 h-8 text-[#1E3A5F]" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-[#0F1C2E] tracking-tight" data-testid="candidate-name">
                  {candidate.name}
                </h1>
                <p className="text-sm text-[#475569] mt-1" data-testid="candidate-position">
                  {candidate.position} - {candidate.state}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge className="bg-[#1E3A5F] text-white text-xs" data-testid="current-party">
                    {candidate.current_party}
                  </Badge>
                  {(candidate.party_history || []).slice(0, 3).map((party, idx) => (
                    <Badge key={idx} variant="outline" className="border-[#1E3A5F] text-[#1E3A5F] text-xs">
                      {party}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-3">
              <IntegrityScore score={candidate.integrity_score || 0} size="md" />
              <RiskBadge risk={candidate.risk_classification} />
              <ExplainButton
                contexto={`nota de integridade do candidato ${candidate.name}`}
                valor={`${(candidate.integrity_score || 0).toFixed(1)} de 10`}
                tipo="percentual"
                fonte="Tô de Olho, com base em dados oficiais do TSE"
                entidade={candidate.name}
                testId="explain-integrity-score"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap gap-2 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              disabled={pdfLoading}
              data-testid="export-pdf-btn"
              className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#F4F6F8]"
            >
              {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Exportar PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportJSON}
              data-testid="export-json-btn"
              className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#F4F6F8]"
            >
              <Download className="w-4 h-4 mr-2" /> Exportar JSON
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={shareUrl ? handleCopyLink : handleShare}
              disabled={sharing}
              data-testid="share-btn"
              className="border-[#2E7D32] text-[#2E7D32] hover:bg-[#E8F5E9]"
            >
              {sharing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando link...</>
              ) : shareUrl ? (
                <>{copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}{copied ? 'Copiado!' : 'Copiar Link'}</>
              ) : (
                <><Share2 className="w-4 h-4 mr-2" /> Compartilhar</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleWhatsApp}
              data-testid="whatsapp-btn"
              className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleTwitter}
              data-testid="twitter-btn"
              className="border-[#1DA1F2] text-[#1DA1F2] hover:bg-[#1DA1F2]/10"
            >
              <Twitter className="w-4 h-4 mr-2" /> Twitter/X
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMonitor}
              data-testid="monitor-btn"
              className="border-[#F57F17] text-[#F57F17] hover:bg-[#F57F17]/10"
            >
              <Eye className="w-4 h-4 mr-2" /> Monitorar
            </Button>
          </div>
          {shareUrl && (
            <div className="mt-2 p-2 bg-[#E8F5E9] rounded-md flex items-center gap-2" data-testid="share-url-display">
              <span className="text-xs text-[#2E7D32] truncate flex-1">{shareUrl}</span>
              <button onClick={handleCopyLink} className="text-[#2E7D32] hover:underline text-xs shrink-0">
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          )}
        </div>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="legal" className="w-full">
        <TabsList className="flex w-full overflow-x-auto bg-white border border-[#E2E8F0] p-1 rounded-md gap-1 no-scrollbar">
          <TabsTrigger value="legal" data-testid="tab-legal" className="shrink-0 data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white text-xs sm:text-sm font-medium px-3">
            <Scale className="w-4 h-4 mr-1" /> Jurídica
          </TabsTrigger>
          <TabsTrigger value="political" data-testid="tab-political" className="shrink-0 data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white text-xs sm:text-sm font-medium px-3">
            <Building2 className="w-4 h-4 mr-1" /> Política
          </TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets" className="shrink-0 data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white text-xs sm:text-sm font-medium px-3">
            <DollarSign className="w-4 h-4 mr-1" /> Patrimônio
          </TabsTrigger>
          <TabsTrigger value="reputation" data-testid="tab-reputation" className="shrink-0 data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white text-xs sm:text-sm font-medium px-3">
            <Newspaper className="w-4 h-4 mr-1" /> Reputação
          </TabsTrigger>
          <TabsTrigger value="tse" data-testid="tab-tse" className="shrink-0 data-[state=active]:bg-[#2E7D32] data-[state=active]:text-white text-xs sm:text-sm font-medium px-3">
            <Database className="w-4 h-4 mr-1" /> TSE
          </TabsTrigger>
          <TabsTrigger value="charts" data-testid="tab-charts" className="shrink-0 data-[state=active]:bg-[#1E3A5F] data-[state=active]:text-white text-xs sm:text-sm font-medium px-3">
            <BarChart3 className="w-4 h-4 mr-1" /> Gráficos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="legal" className="mt-4">
          <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md">
            <h3 className="text-xl font-bold text-[#0F1C2E] mb-5">Situação Jurídica</h3>
            <div className="flex flex-wrap gap-3 mb-5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider">Ficha Limpa</span>
                <Badge className={legal.ficha_limpa_compliant ? 'bg-[#2E7D32] text-white' : 'bg-[#C62828] text-white'}>
                  {legal.ficha_limpa_compliant ? 'Conforme' : 'Não conforme'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider">Elegibilidade</span>
                <Badge className="bg-[#1E3A5F] text-white">{legal.eligibility_status || 'Não verificado'}</Badge>
              </div>
            </div>
            <Section title="Processos Judiciais">
              <ItemList items={legal.processes} borderColor="#E2E8F0" />
            </Section>
            <Section title="Condenações">
              <ItemList items={legal.convictions} borderColor="#C62828" />
            </Section>
            <Section title="Investigações">
              <ItemList items={legal.investigations} borderColor="#F57F17" />
            </Section>
          </Card>
        </TabsContent>

        <TabsContent value="political" className="mt-4">
          <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md">
            <h3 className="text-xl font-bold text-[#0F1C2E] mb-5">Histórico Político</h3>
            <Section title="Cargos Anteriores">
              <ItemList items={political.previous_positions} borderColor="#1E3A5F" />
            </Section>
            <Section title="Principais Decisões">
              <ItemList items={political.key_decisions} borderColor="#2E7D32" />
            </Section>
            <Section title="Controvérsias">
              <ItemList items={political.controversies} borderColor="#C62828" />
            </Section>
          </Card>
        </TabsContent>

        <TabsContent value="assets" className="mt-4">
          <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md">
            <h3 className="text-xl font-bold text-[#0F1C2E] mb-5">Patrimônio</h3>
            <Section title="Evolução Patrimonial">
              <p className="text-sm text-[#475569]" data-testid="asset-evolution">{assets.asset_evolution || 'Dados não disponíveis'}</p>
            </Section>
            <Section title="Declarações de Bens">
              <ItemList items={assets.declarations} borderColor="#2E7D32" />
            </Section>
            <Section title="Inconsistências">
              <ItemList items={assets.inconsistencies} borderColor="#C62828" />
            </Section>
          </Card>
        </TabsContent>

        <TabsContent value="reputation" className="mt-4">
          <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md">
            <h3 className="text-xl font-bold text-[#0F1C2E] mb-5">Reputação</h3>
            <Section title="Cobertura da Mídia">
              <p className="text-sm text-[#475569]" data-testid="media-coverage">{reputation.media_coverage || 'Dados não disponíveis'}</p>
            </Section>
            <Section title="Percepção Pública">
              <p className="text-sm text-[#475569]" data-testid="public-perception">{reputation.public_perception || 'Dados não disponíveis'}</p>
            </Section>
            <Section title="Avaliações Independentes">
              <ItemList items={reputation.independent_ratings} borderColor="#1E3A5F" />
            </Section>
          </Card>
        </TabsContent>

        <TabsContent value="tse" className="mt-4">
          <TseDataPanel candidateName={candidate.name} candidateState={candidate.state} />
        </TabsContent>

        <TabsContent value="charts" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <IntegrityRadarChart candidate={candidate} />
            <ScoreBreakdownChart candidate={candidate} />
            <WeightBreakdownChart />
          </div>
        </TabsContent>
      </Tabs>

      {/* Conclusion */}
      <Card className="p-5 bg-[#F4F6F8] border border-[#E2E8F0] rounded-md">
        <h3 className="text-lg font-bold text-[#0F1C2E] mb-3">Conclusão</h3>
        <p className="text-sm text-[#475569] leading-relaxed" data-testid="conclusion">
          {candidate.conclusion || 'Análise não disponível.'}
        </p>
      </Card>

      {/* Sources */}
      <Card className="p-5 bg-white border border-[#E2E8F0] rounded-md">
        <h3 className="text-lg font-bold text-[#0F1C2E] mb-3 flex items-center gap-2">
          <ExternalLink className="w-4 h-4" /> Fontes
        </h3>
        {(candidate.sources || []).length > 0 ? (
          <ul className="space-y-1" data-testid="sources-list">
            {candidate.sources.map((source, idx) => (
              <li key={idx} className="text-sm text-[#1E3A5F]">{source}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-[#94A3B8] italic">Nenhuma fonte listada</p>
        )}
      </Card>

      {/* Sticky candidate footer — visible while scrolling the analysis page.
          Mobile: sits above the bottom tab bar (h-16=64px) + safe-area-inset.
          Desktop (lg+): sits flush at the bottom and respects the sidebar (lg:ml-64). */}
      <div
        className="fixed left-0 right-0 bottom-16 lg:bottom-0 lg:left-64 z-40 bg-white border-t border-[#E2E8F0] shadow-[0_-2px_8px_rgba(15,28,46,0.06)]"
        style={{ paddingBottom: 'var(--safe-bottom, 0px)' }}
        data-testid="candidate-sticky-footer"
      >
        <div className="max-w-[1440px] mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#F4F6F8] flex items-center justify-center shrink-0 overflow-hidden">
            {candidate.photo_url ? (
              <img
                src={candidate.photo_url}
                alt={candidate.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <User2 className="w-5 h-5 text-[#1E3A5F]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-sm font-bold text-[#0F1C2E] truncate"
              data-testid="sticky-footer-name"
            >
              {candidate.name}
            </p>
            <p className="text-[11px] text-[#475569] truncate">
              {[candidate.current_party, candidate.position, candidate.state]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={handleShare}
            disabled={sharing}
            className="bg-[#006030] hover:bg-[#004020] text-white shrink-0"
            data-testid="sticky-footer-share"
          >
            {sharing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Share2 className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Compartilhar</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CandidateProfile;
